import { useState, useEffect } from 'react';
import { getUserSettings, updateUserSettings, requestApiKey, deleteApiKey } from '../../service/userService';
import componentStyles from './settings.module.css';
import globalStyles from '../globals.module.css';
import SessionService from '../../service/sessionService';
import SmallLoadingAlt from '../global/smallLoadingAlt';
import eventBus from '../../service/eventBus';
import { EVENT_UPDATE_ACTION_BAR_VIEW } from '../../utils/constants';
import PageView from '../common/pageView';
import AnalyticsService from '../../service/analyticsService';
import { EVENT_SETTINGS_SAVED } from '../../utils/analyticsConstants';
import AccessService from '../../service/accessService';
import AccessLockedPanel from '../common/accessLockedPanel';

const Settings = () => {
    // State for all settings
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');
    const sessionService = SessionService.getInstance();
    const accessService = AccessService.getInstance();
    const access = accessService.getAccess();
    const canUseApiKeys = accessService.isProOrAdmin();
    const [settings, setSettings] = useState(sessionService.getUserSettings());

    const [showSportPreferences, setShowSportPreferences] = useState(false);
    const [showApiKeyManagement, setShowApiKeyManagement] = useState(false);
    // State for API key management
    const [apiKeyLoading, setApiKeyLoading] = useState(true);
    const [apiKeyActionLoading, setApiKeyActionLoading] = useState(false);
    const [apiSettings, setApiSettings] = useState([]);
    const [copiedIndex, setCopiedIndex] = useState(-1);
    
    // Fetch user settings on component mount
    useEffect(() => {
        // Fetch both user settings and API key info on mount
        const fetchAll = async () => {
            try {
                const userId = sessionService.getUserId();
                // Load user settings
                try {
                    const response = await getUserSettings(userId);
                    const { data } = response;
                    if (data.success) {
                        const { results } = data;
                        setSettings(results.userSettings);
                        const {apiSettings} = results;
                        if (apiSettings) {
                            setApiSettings(apiSettings);
                        }
                        setApiKeyLoading(false);
                    } else {
                        setSaveStatus('Error loading settings');
                    }
                } catch (error) {
                    setSaveStatus('Error loading settings');
                }

                // Load API key info
                // try {
                //     setApiKeyLoading(false);
                //     const apiKeyResponse = await getApiKeyInfo(userId);
                //     const { data: apiData } = apiKeyResponse;
                //     if (apiData && apiData.success) {
                //         const { results } = apiData;
                //         setApiKeyInfoState({
                //             exists: !!results.exists,
                //             key: results.key || null,
                //             creditsUsed: Number(results.creditsUsed || 0),
                //             creditsLimit: Number(results.creditsLimit || 0),
                //         });
                //     } else {
                //         setApiKeyInfoState({ exists: false, key: null, creditsUsed: 0, creditsLimit: 0 });
                //     }
                // } catch (error) {
                //     setApiKeyInfoState({ exists: false, key: null, creditsUsed: 0, creditsLimit: 0 });
                // } finally {
                //     setApiKeyLoading(false);
                // }
            } catch (_) {
                // no-op
            }
        };
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle sports toggle
    const handleSportToggle = (sport) => {
        console.log(`handleSportToggle: ${sport}`);
        setSettings(prev => ({
            ...prev,
            sports: {
                ...prev.sports,
                [sport]: !prev.sports[sport]
            }
        }));
    };

    // Handle bet type toggle
    const handleBetTypeToggle = (betType) => {
        console.log(`handleBetTypeToggle: ${betType}`);
        setSettings(prev => ({
            ...prev,
            betTypes: {
                ...prev.betTypes,
                [betType]: !prev.betTypes[betType]
            }
        }));
    };

    // Handle feed preferences change
    const handleFeedPreferenceChange = (key, value) => {
        console.log(`handleFeedPreferenceChange: ${key} ${value}`);
        setSettings(prev => ({
            ...prev,
            feedPreferences: {
                ...prev.feedPreferences,
                [key]: value
            }
        }));
    };

    // Handle notification toggle
    const handleNotificationToggle = (key) => {
        console.log(`handleNotificationToggle: ${key}`);
        setSettings(prev => ({
            ...prev,
            notifications: {
                ...prev.notifications,
                [key]: !prev.notifications[key]
            }
        }));
    };

    // Save settings
    const handleSave = async () => {
        setIsSaving(true);
        setSaveStatus('');
        try {
            const userId = sessionService.getUserId();
            const response = await updateUserSettings(userId, settings);
            if (response.data.success) {
                AnalyticsService.getInstance().track(EVENT_SETTINGS_SAVED, { section: 'sports' });
                const {results} = response.data;
                setSaveStatus('Settings saved successfully!');
                sessionService.setUserSettings(results.userSettings);
                setSettings(results.userSettings);
                eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, 'props');
            }
        } catch (error) {
            setSaveStatus('Error saving settings');
        } finally {
            setIsSaving(false);
        }
    };

    // Request a new API key for the user
    const handleRequestApiKey = async () => {
        if (!canUseApiKeys) {
            return;
        }

        setApiKeyActionLoading(true);
        setCopiedIndex(-1);
        try {
            const userId = sessionService.getUserId();
            const response = await requestApiKey(userId);
            const { data } = response;
            if (data && data.success) {
                const { results } = data;
                // Append the newly created key to the list
                const newItem = {
                    periodStart: results.periodStartDate || '',
                    key: results.apiKey,
                    creditsUsed: Number(results.requestsUsed || 0),
                    creditsLimit: Number(results.monthlyLimit || 0),
                };
                setApiSettings(prev => Array.isArray(prev) ? [...prev, newItem] : [newItem]);
            }
        } catch (error) {
            // swallow; UI shows no change
        } finally {
            setApiKeyActionLoading(false);
        }
    };

    // Delete the existing API key
    const handleDeleteApiKey = async () => {
        if (!canUseApiKeys) {
            return;
        }

        setApiKeyActionLoading(true);
        setCopiedIndex(-1);
        try {
            const userId = sessionService.getUserId();
            const response = await deleteApiKey(userId);
            const { data } = response;
            if (data && data.success) {
                // After delete, clear keys list
                setApiSettings([]);
            }
        } catch (error) {
            // swallow; UI shows no change
        } finally {
            setApiKeyActionLoading(false);
        }
    };

    // Copy API key to clipboard and show ephemeral copied state
    const handleCopyApiKey = async (keyString, index) => {
        try {
            if (keyString) {
                await navigator.clipboard.writeText(keyString);
                setCopiedIndex(index);
                setTimeout(() => setCopiedIndex(-1), 1500);
            }
        } catch (_) {
            // noop
        }
    };

    // Render the API Key Management section
    const renderApiKeyManagement = () => {
        if (access.tier === 'free') {
            return null;
        }

        const totals = (Array.isArray(apiSettings) ? apiSettings : []).reduce((acc, item) => {
            const used = Number(item.requestsUsed || 0);
            const limit = Number(item.monthlyLimit || 0);
            return { used: acc.used + used, limit: acc.limit + limit };
        }, { used: 0, limit: 0 });
        const remaining = Math.max(0, totals.limit - totals.used);
        return (
            <section className={`${globalStyles.standardSection} ${componentStyles.settingsSection}`}>
                <div className={globalStyles.menuGroup}>
                    <h3>API Key Management</h3>                
                    <button className={globalStyles.secondaryButton} onClick={() => setShowApiKeyManagement(!showApiKeyManagement)}>
                        {showApiKeyManagement ? 'Hide' : 'Show'}
                    </button>
                </div>
                {showApiKeyManagement && (
                    <div className={componentStyles.apiKeyContainer}>
                    {!canUseApiKeys ? (
                        <AccessLockedPanel
                            title={access.tier === 'anonymous' ? 'Login Required' : 'API Access Locked'}
                            message={access.tier === 'anonymous'
                                ? 'Log in to view API access options.'
                                : 'Upgrade to Pro to create API keys and access API endpoints.'}
                            actionLabel={access.tier === 'anonymous' ? 'Log In' : 'Upgrade to Pro'}
                            actionView={access.tier === 'anonymous' ? 'login' : 'profile'}
                        />
                    ) : (
                        <>
                    {apiKeyLoading ? (
                        <SmallLoadingAlt />
                    ) : (
                        <>
                            {(!apiSettings || apiSettings.length === 0) ? (
                                <>
                                    <p className={componentStyles.helperText}>
                                        Request your personal API key to access our endpoints. Keys include a monthly credit allotment; usage resets on the 1st of each month.
                                    </p>
                                    <button
                                        className={globalStyles.primaryButton}
                                        onClick={handleRequestApiKey}
                                        disabled={apiKeyActionLoading}
                                    >
                                        {apiKeyActionLoading ? 'Requesting…' : 'Request API Key'}
                                    </button>
                                </>
                            ) : (
                                <>
                                    {(apiSettings || []).map((item, index) => (
                                        <div key={item.periodStart || `${item.key}-${index}`} className={componentStyles.apiKeyRow}>
                                            <div className={componentStyles.apiKeyBox} title="Your API key">
                                                {item.apiKey || ''}
                                            </div>
                                            <button
                                                className={globalStyles.secondaryButton}
                                                onClick={() => handleCopyApiKey(item.apiKey, index)}
                                                disabled={!item.apiKey}
                                            >
                                                {copiedIndex === index ? 'Copied' : 'Copy'}
                                            </button>
                                        </div>
                                    ))}
                                    <button className={globalStyles.secondaryButton} onClick={handleDeleteApiKey} disabled={apiKeyActionLoading}>
                                        {apiKeyActionLoading ? 'Deleting…' : 'Delete Key'}
                                    </button>
                                    <div className={componentStyles.creditsText}>
                                        Credits Remaining: {remaining}/{totals.limit}
                                    </div>
                                </>
                            )}
                        </>
                        )}
                        </>
                    )}
                    </div>
                )}
            </section>
        );
    };

    const renderBetTypes = () => {
        return (
            <section className={`${globalStyles.standardSection} ${componentStyles.settingsSection}`}>
                <h3 className={globalStyles.sectionHeader}>Bet Types</h3>
                <div className={componentStyles.checkboxGrid}>
                    {Object.entries(settings.betTypes).map(([betType, isEnabled]) => (
                        <label key={betType} className={componentStyles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={isEnabled}
                                onChange={() => handleBetTypeToggle(betType)}
                                className={globalStyles.standardCheckbox}
                            />
                            <span className={componentStyles.checkboxText}>
                                {betType.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                        </label>
                    ))}
                </div>
            </section>
        )
    }

    const renderFeedPreferences = () => {
        return (
            <section className={`${globalStyles.standardSection} ${componentStyles.settingsSection}`}>
                <h3 className={globalStyles.sectionHeader}>Feed Preferences</h3>
                <div className={componentStyles.inputGroup}>
                    <div className={componentStyles.selectGroup}>
                        <label>Items per page</label>
                        <select
                            value={settings.feedPreferences.itemsPerPage}
                            onChange={(e) => handleFeedPreferenceChange('itemsPerPage', Number(e.target.value))}
                            className={globalStyles.standardSelect}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <label className={componentStyles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={settings.feedPreferences.autoRefresh}
                                onChange={() => handleFeedPreferenceChange('autoRefresh', !settings.feedPreferences.autoRefresh)}
                                className={globalStyles.standardCheckbox}
                            />
                        <span className={componentStyles.checkboxText}>Auto-refresh feed</span>
                    </label>
                    {settings.feedPreferences.autoRefresh && (
                        <div className={componentStyles.selectGroup}>
                            <label>Refresh interval (minutes)</label>
                            <select
                                value={settings.feedPreferences.refreshInterval}
                                onChange={(e) => handleFeedPreferenceChange('refreshInterval', Number(e.target.value))}
                                className={globalStyles.standardSelect}
                            >
                                <option value={1}>1</option>
                                <option value={5}>5</option>
                                <option value={15}>15</option>
                                <option value={30}>30</option>
                            </select>
                        </div>
                    )}
                </div>
            </section>
        )
    }

    const renderNotificationPreferences = () => {
        return (
            <section className={`${globalStyles.standardSection} ${componentStyles.settingsSection}`}>
                <h3 className={globalStyles.sectionHeader}>Notifications</h3>
                <div className={componentStyles.checkboxGrid}>
                    {Object.entries(settings.notifications).map(([key, isEnabled]) => (
                        <label key={key} className={componentStyles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={isEnabled}
                                onChange={() => handleNotificationToggle(key)}
                                className={globalStyles.standardCheckbox}
                            />
                            <span className={componentStyles.checkboxText}>
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                        </label>
                    ))}
                </div>
            </section>
        )
    }

    return (
        <PageView
            title="Settings"
            containerClassName={componentStyles.settingsContainer}
            contentClassName={componentStyles.settingsContent}
        >
            
            {/* Sports Section */}
            <section className={`${globalStyles.standardSection} ${componentStyles.settingsSection}`}>
                <div className={globalStyles.menuGroup}>
                    <h3>Sports Preferences</h3>
                    <button className={globalStyles.secondaryButton} onClick={() => setShowSportPreferences(!showSportPreferences)}>
                        {showSportPreferences ? 'Hide' : 'Show'}
                    </button>
                </div>
                {showSportPreferences && (
                    <div className={componentStyles.checkboxGrid}>
                    {Object.entries(settings.sports).map(([sport, isEnabled]) => (
                        <label key={sport} className={componentStyles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={isEnabled}
                                onChange={() => handleSportToggle(sport)}
                                className={globalStyles.standardCheckbox}
                            />
                            <span className={componentStyles.checkboxText}>
                                {sport.toUpperCase()}
                            </span>
                        </label>
                        ))}
                    </div>
                )}
            </section>

            {/* Bet Types Section */}
            {/* {renderBetTypes()} */}

            {/* Feed Preferences Section */}
            {/* {renderFeedPreferences()} */}

            {/* Notifications Section */}
            {/* {renderNotificationPreferences()} */}

            {/* API Key Management Section */}
            {renderApiKeyManagement()}

            {/* Save Button */}
                <button 
                    className={globalStyles.primaryButton}
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
                {saveStatus && (
                    <div className={componentStyles.saveStatus}>
                        {saveStatus}
                    </div>
                )}
        </PageView>
    );
};

export default Settings;
